/**
 * Session Token Bootstrap
 * Manages session tokens across all MPA pages
 */

const SESSION_TOKEN_COOKIE = 'session_token';

class SessionBootstrap {
  constructor() {
    this.tokenPromise = null;
    this.isInitialized = false;
  }

  /**
   * Initialize session token management
   * Call this from every page entry point
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    
    try {
      await this.ensureSessionToken();
      this.logEvent('SESSION_BOOTSTRAP_SUCCESS', 'Session token initialized');
    } catch (error) {
      this.logEvent('SESSION_BOOTSTRAP_ERROR', `Failed to initialize: ${error.message}`);
      throw error;
    }
  }

  /**
   * Ensure session token exists, generate if missing
   */
  async ensureSessionToken() {
    const existingToken = this.getSessionToken();
    
    if (existingToken && this.isValidToken(existingToken)) {
      this.logEvent('SESSION_TOKEN_EXISTS', 'Using existing valid token');
      return existingToken;
    }

    // Prevent multiple simultaneous token requests
    if (this.tokenPromise) {
      return await this.tokenPromise;
    }

    this.tokenPromise = this.generateNewToken();
    
    try {
      const token = await this.tokenPromise;
      this.tokenPromise = null;
      return token;
    } catch (error) {
      this.tokenPromise = null;
      throw error;
    }
  }

  /**
   * Get session token from HttpOnly cookie
   */
  getSessionToken() {
    // Try to get from HttpOnly cookie first (server-side set)
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === SESSION_TOKEN_COOKIE) {
        return value;
      }
    }
    
    // Fallback to localStorage for development
    return localStorage.getItem(SESSION_TOKEN_COOKIE);
  }

  /**
   * Set session token with secure attributes
   */
  setSessionToken(token) {
    // Set HttpOnly cookie (requires server-side implementation)
    // For now, use secure localStorage as fallback
    localStorage.setItem(SESSION_TOKEN_COOKIE, token);
    
    // Also set regular cookie for immediate access
    const expires = new Date();
    expires.setHours(expires.getHours() + 24); // 24 hour expiry
    
    document.cookie = `${SESSION_TOKEN_COOKIE}=${token}; expires=${expires.toUTCString()}; path=/; secure; samesite=strict`;
  }

  /**
   * Basic token validation
   */
  isValidToken(token) {
    if (!token || typeof token !== 'string') {
      return false;
    }
    
    // Basic format validation (adjust based on actual token format)
    return token.length > 10 && !token.includes('undefined');
  }

  /**
   * Get or generate persistent UUID for this session
   */
  getSessionUUID() {
    let uuid = localStorage.getItem('session_uuid');
    if (!uuid) {
      uuid = this.generateUUID();
      localStorage.setItem('session_uuid', uuid);
      this.logEvent('UUID_GENERATED', `New session UUID: ${uuid}`);
    }
    return uuid;
  }

  /**
   * Update UUID when user logs in (called from login success)
   */
  updateUserUUID(userUuid, email = null, brand = null) {
    if (userUuid && userUuid !== this.getSessionUUID()) {
      localStorage.setItem('session_uuid', userUuid);
      if (email) localStorage.setItem('user_email', email);
      if (brand) localStorage.setItem('user_brand', brand);
      this.logEvent('UUID_UPDATED', `UUID updated for logged in user: ${userUuid}`);
      // Clear existing token so new one gets generated with new UUID
      this.clearSession();
    }
  }

  /**
   * Generate new session token from API (direct call, not through proxy)
   */
  async generateNewToken() {
    this.logEvent('TOKEN_REQUEST_START', 'Requesting new session token');
    
    try {
      const tokenUrl = process.env.VUE_APP_TOKEN_URL || 'https://api.rezcomm.com/authentication/central/token';
      
      // Use persistent UUID for the token request
      const uuid = this.getSessionUUID();
      const email = localStorage.getItem('user_email');
      const brand = localStorage.getItem('user_brand') || localStorage.getItem('default_brand');
      
      const requestBody = { uuid };
      if (email) requestBody.email = email;
      if (brand) requestBody.brand = brand;
      
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.token) {
        throw new Error('No token received from API');
      }

      this.setSessionToken(data.token);
      this.logEvent('TOKEN_REQUEST_SUCCESS', `New session token generated for UUID: ${uuid}`);
      
      return data.token;
    } catch (error) {
      this.logEvent('TOKEN_REQUEST_ERROR', `Failed to generate token: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get current session token (for use by other services)
   */
  getCurrentToken() {
    return this.getSessionToken();
  }

  /**
   * Log session events for monitoring
   */
  logEvent(event, message) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      event,
      message,
      page: window.location.pathname,
      userAgent: navigator.userAgent.substring(0, 100)
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('[SessionBootstrap]', logEntry);
    }

    // Store recent events for debugging
    const recentEvents = JSON.parse(localStorage.getItem('session_events') || '[]');
    recentEvents.push(logEntry);
    
    // Keep only last 10 events
    if (recentEvents.length > 10) {
      recentEvents.shift();
    }
    
    localStorage.setItem('session_events', JSON.stringify(recentEvents));
  }

  /**
   * Generate a UUID v4
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Clear session token (for logout)
   */
  clearSession() {
    localStorage.removeItem(SESSION_TOKEN_COOKIE);
    localStorage.removeItem('session_uuid');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_brand');
    document.cookie = `${SESSION_TOKEN_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    this.logEvent('SESSION_CLEARED', 'Session token and UUID cleared');
  }
}

// Create singleton instance
const sessionBootstrap = new SessionBootstrap();

export default sessionBootstrap;